import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { forgotPassword } from '../services/api';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      // The backend returns a generic response whether or not the account
      // exists (anti-enumeration), so we always show the same confirmation.
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen dark:bg-slate-900 bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="sm:mx-auto sm:w-full sm:max-w-md"
      >
        <Link to="/" className="flex justify-center mb-6">
          <img className="h-32 w-auto" src="/AutoHire.png" alt="AutoHire" />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold dark:text-white text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm dark:text-gray-400 text-gray-600">
          Remember it?{' '}
          <Link to="/login" className="font-medium text-blue-500 hover:text-blue-400">
            Back to sign in
          </Link>
        </p>
      </motion.div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="dark:bg-slate-800 bg-white py-8 px-4 shadow-xl border dark:border-white/10 border-gray-200 sm:rounded-lg sm:px-10">
          {submitted ? (
            <div className="text-center">
              <p className="text-sm dark:text-gray-300 text-gray-700">
                If an account exists for <span className="font-medium">{email}</span>, we've sent a
                password reset link. The link expires in 1 hour and can only be used once.
              </p>
              <p className="mt-4 text-sm dark:text-gray-400 text-gray-500">
                Didn't get it? Check your spam folder, or{' '}
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="font-medium text-blue-500 hover:text-blue-400"
                >
                  try again
                </button>
                .
              </p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <p className="text-sm dark:text-gray-400 text-gray-600">
                Enter the email associated with your account and we'll send you a link to reset your
                password.
              </p>
              <div>
                <label htmlFor="email" className="block text-sm font-medium dark:text-gray-300 text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-3 border dark:border-gray-600 border-gray-300 dark:bg-slate-900 bg-white rounded-lg shadow-sm placeholder-gray-400 dark:text-white text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white gradient-bg hover:shadow-lg hover:shadow-blue-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all transform hover:-translate-y-0.5"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
